/**
 * Safe Intl helpers — invalid currency/date values throw RangeError and take down the whole SPA
 * because there is no ErrorBoundary around most routes.
 */

const FALLBACK_CURRENCY = 'IDR';

export function isValidCurrencyCode(currency: string | null | undefined): boolean {
  if (!currency || currency.length !== 3) {
    return false;
  }
  try {
    new Intl.NumberFormat('en', { style: 'currency', currency }).format(0);
    return true;
  } catch {
    return false;
  }
}

export function formatMoneySafe(
  amount: number,
  currency?: string | null,
  locale = 'id-ID',
  options?: { showCurrency?: boolean },
): string {
  const code = isValidCurrencyCode(currency) ? currency!.toUpperCase() : FALLBACK_CURRENCY;
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const showCurrency = options?.showCurrency !== false;
  try {
    if (!showCurrency) {
      return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(safeAmount);
    }
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(safeAmount);
  } catch {
    return showCurrency
      ? `${safeAmount.toLocaleString(locale)} ${code}`
      : safeAmount.toLocaleString(locale);
  }
}

export function toValidDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateSafe(
  value: string | Date | null | undefined,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toValidDate(value);
  if (!date) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return date.toISOString();
  }
}
